import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DotsVerticalIcon, PencilIcon, TrashIcon, PlusIcon, ChevronDownIcon, MailIcon, DocumentTextIcon, CogIcon, PlayIcon, SwitchHorizontalIcon, XIcon, CollectionIcon } from '@heroicons/react/outline';
import { Modal, Input, message, Tooltip, Popover, Switch } from 'antd';
import styled from 'styled-components';
import BoringAvatar from 'boring-avatars';
import { useProject } from '../contexts/ProjectContext';
import { Project, projectsService } from '../services/projectsService';
import { researchTableService, type ResearchTable } from '../services/researchTableService';
import { navigationService } from '../services/navigationService';
import { OutreachTemplateModal } from '../components/ResearchTable/OutreachTemplateModal';
import { ProjectOverlay } from './ProjectOverlay';
import { ResourceStudioModal } from '../components/ResearchTable/ResourceStudioModal';
import { resourcesService, Resource } from '../services/resourcesService';
import { colors, spacing, typography } from '../design-system/tokens';
import { WorkflowPopover } from '../components/WorkflowContext/WorkflowPopover';

// Styled components
const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 16px 4px 16px;
  background: transparent;
  position: sticky;
  top: 0;
  z-index: 20;
  min-height: 36px;
  overflow: visible;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  overflow-y: visible;
  flex: 1;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;


const ProjectTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ProjectTab = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${props => props.$active ? '500' : '400'};
  color: ${props => props.$active ? '#374151' : '#6b7280'};
  transition: all 0.15s ease;
  white-space: nowrap;
  
  &:hover {
    color: ${props => props.$active ? '#374151' : '#4b5563'};
  }
  
  &:focus {
    outline: none;
  }
`;

const ProjectSelector = styled.div`
  position: relative;
`;

const ProjectSelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
  transition: all 0.15s ease;
  min-width: 160px;
  justify-content: space-between;
  
  &:hover {
    color: #374151;
    background: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(4px);
  }
  
  &:focus {
    outline: none;
  }
  
  svg {
    opacity: 0.6;
    transition: all 0.15s ease;
  }
  
  &:hover svg {
    opacity: 1;
  }
`;

const ProjectDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  min-width: 240px;
  z-index: 100;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-4px)'};
  transition: all 0.2s ease;
  max-height: 320px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.15);
  }
`;

const ProjectOption = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 16px;
  background: ${props => props.$selected ? 'rgba(59, 130, 246, 0.05)' : 'transparent'};
  cursor: pointer;
  font-size: 13px;
  color: ${props => props.$selected ? '#3b82f6' : '#374151'};
  text-align: left;
  transition: all 0.15s ease;
  border-left: 2px solid ${props => props.$selected ? '#3b82f6' : 'transparent'};
  
  &:hover {
    background: rgba(0, 0, 0, 0.02);
    color: #1f2937;
  }
  
  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }
  
  &:last-of-type:not(:has(+ *)) {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const ProjectNameField = styled.div<{ $isEditing?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  font-weight: ${props => props.$isEditing ? '400' : '500'};
  
  input {
    border: none;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 13px;
    font-weight: 500;
    width: 100%;
    color: #374151;
    
    &:focus {
      outline: none;
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
    }
  }
`;

const ProjectActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: all 0.2s ease;
  
  ${ProjectOption}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
  
  svg {
    width: 14px;
    height: 14px;
    color: #9ca3af;
    transition: color 0.15s ease;
  }
  
  &:hover svg {
    color: #6b7280;
  }
`;

const NewProjectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  text-align: left;
  transition: all 0.15s ease;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  margin-top: 4px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.02);
    color: #374151;
  }
  
  svg {
    width: 14px;
    height: 14px;
    opacity: 0.7;
  }
  
  &:hover svg {
    opacity: 1;
  }
`;




// Project actions styled components (moved from ResearchTablesV2)
const ProjectHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(4px);
    color: #374151;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(4px);
    color: #374151;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const SettingsDropdownContainer = styled.div`
  position: relative;
`;

const SettingsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(4px);
    color: #334155;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const SettingsDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  min-width: 160px;
  z-index: 50;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-4px)'};
  transition: all 0.2s ease;
`;

const SettingsMenuItem = styled.button<{ $variant?: 'danger' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: ${props => props.$variant === 'danger' ? '#dc2626' : '#374151'};
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  
  &:first-child {
    border-radius: 6px 6px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 6px 6px;
  }
  
  &:only-child {
    border-radius: 6px;
  }
  
  &:hover {
    background: ${props => props.$variant === 'danger' ? '#fef2f2' : '#f9fafb'};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Management Sidebar Components
const ManagementSidebar = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 50px;
  right: 0;
  width: 280px;
  height: calc(100vh - 50px);
  background: #ffffff;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.08);
  transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: #fafbfc;
`;

const SidebarTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 0;
`;

const StageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const StageItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${props => props.$active ? '500' : '400'};
  color: ${props => props.$active ? '#374151' : '#6b7280'};
  transition: all 0.15s ease;
  text-align: left;
  
  &:hover {
    background: rgba(0, 0, 0, 0.03);
    color: #374151;
  }
`;

const StageIcon = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const AddStageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
  transition: all 0.15s ease;
  text-align: left;
  margin-top: 8px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.03);
    color: #374151;
  }
`;

const SidebarOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  z-index: 999;
`;

// Template display components - REMOVED (Interest Request and Brief no longer needed)

// View Mode Toggle Components - Subtle design
const ViewModeToggle = styled.div`
  display: flex;
  background: transparent;
  gap: 2px;
  align-items: center;
`;

const ViewModeButton = styled.button<{ $active: boolean }>`
  padding: 4px 10px;
  border: none;
  background: transparent;
  font-size: 11px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.15s ease;
  color: ${props => props.$active ? colors.text.primary : colors.text.tertiary};
  opacity: ${props => props.$active ? 1 : 0.6};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 10px;
    right: 10px;
    height: 1px;
    background: ${props => props.$active ? colors.text.primary : 'transparent'};
    transition: all 0.15s ease;
  }
  
  &:hover {
    color: ${colors.text.primary};
    opacity: 1;
  }
`;

const ProjectMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  color: #374151;
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  
  &:first-child {
    border-radius: 6px 6px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 6px 6px;
  }
  
  &:only-child {
    border-radius: 6px;
  }
  
  &:hover {
    background: #f9fafb;
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: #6b7280;
  }
`;

const ProjectSeparator = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
`;

const ProjectDropdownLeft = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  min-width: 200px;
  z-index: 9999;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-4px)'};
  transition: all 0.2s ease;
`;

// Removed companion-related styled components


interface ProjectHeaderProps {
  rightContent?: React.ReactNode;
  tables: ResearchTable[];
  activeTableId: string | null;
  tablesLoading: boolean;
  onStageClick: (tableId: string) => void;
  onCreateStage: () => void;
  onDeleteTable: (tableId: string) => void;
  // Table-specific actions removed - these should be handled at table level
  onManageTemplates?: () => void;
  onSendOutreach?: () => void;
  currentTableCreators?: any[];
  onAnalyzeOutreach?: (templateId: string, creators: any[]) => void;
}

const ProjectHeaderComponent: React.FC<ProjectHeaderProps> = ({ 
  rightContent,
  tables,
  activeTableId,
  tablesLoading,
  onStageClick,
  onCreateStage,
  onDeleteTable,
  // Table-specific actions removed
  onManageTemplates,
  onSendOutreach,
  currentTableCreators = [],
  onAnalyzeOutreach
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    selectedProject,
    projects,
    loadProjects 
  } = useProject();
  
  const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [workflowPopoverOpen, setWorkflowPopoverOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<{id: string, name: string} | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);
  const [showTemplateStudio, setShowTemplateStudio] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState<Resource | null>(null);
  const [templateTitle, setTemplateTitle] = useState<string>('');
  const [projectTemplates, setProjectTemplates] = useState<Resource[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [projectTables, setProjectTables] = useState<ResearchTable[]>([]);
  const [projectTablesLoading, setProjectTablesLoading] = useState(false);
  
  // Check current route
  const isManagementPage = location.pathname.startsWith('/projects/') && 
                           location.pathname !== '/projects';

  // Fetch project templates
  const fetchProjectTemplates = async () => {
    if (!selectedProject) return;
    
    setTemplatesLoading(true);
    try {
      const allResources = await resourcesService.getResources();
      // Filter resources that are associated with the current project
      const projectResources = allResources.filter(resource => 
        resource.associated_projects?.some(proj => proj.project_uuid === selectedProject.project_uuid)
      );
      setProjectTemplates(projectResources);
    } catch (error) {
      console.error('Error fetching project templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Fetch templates when project changes
  useEffect(() => {
    if (selectedProject) {
      fetchProjectTemplates();
    }
  }, [selectedProject]);

  // Fetch project tables
  const fetchProjectTables = async () => {
    if (!selectedProject) {
      setProjectTables([]);
      return;
    }
    
    setProjectTablesLoading(true);
    try {
      const tables = await researchTableService.getResearchTables(selectedProject.project_uuid);
      setProjectTables(tables || []);
    } catch (error) {
      console.error('Error fetching project tables:', error);
      setProjectTables([]);
    } finally {
      setProjectTablesLoading(false);
    }
  };

  // Fetch tables when project changes
  useEffect(() => {
    if (selectedProject) {
      fetchProjectTables();
    }
  }, [selectedProject]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const settingsDropdown = target.closest('[data-settings-dropdown]');
      const projectDropdown = target.closest('[data-project-dropdown]');
      if (settingsDropdownOpen && !settingsDropdown) {
        setSettingsDropdownOpen(false);
      }
      
      if (projectDropdownOpen && !projectDropdown) {
        setProjectDropdownOpen(false);
      }
    };

    if (settingsDropdownOpen || projectDropdownOpen) {
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 10);
      
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [settingsDropdownOpen, projectDropdownOpen]);



  // Project action handlers (for list view)
  const handleSettingsToggle = () => {
    setSettingsDropdownOpen(!settingsDropdownOpen);
  };

  const handleCreateStageClick = () => {
    setSettingsDropdownOpen(false);
    onCreateStage();
  };

  const handleManageStagesClick = () => {
    setSettingsDropdownOpen(false);
    navigate(`/projects/${selectedProject?.project_uuid}`);
  };


  const handleEditProject = () => {
    setSettingsDropdownOpen(false);
    // TODO: Implement edit project modal
    console.log('Edit project:', selectedProject?.name);
  };

  const handleDeleteProject = () => {
    setSettingsDropdownOpen(false);
    // TODO: Implement delete project modal
    console.log('Delete project:', selectedProject?.name);
  };


  const handleManagementClick = () => {
    if (selectedProject) {
      navigate(navigationService.project(selectedProject.project_uuid));
    }
  };

  const handleProjectSelect = (project: Project) => {
    setProjectDropdownOpen(false);
    // Always navigate to management page since assistant is removed
    navigate(navigationService.project(project.project_uuid));
  };

  const handleEditProjectClick = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingProject({ id: project.project_uuid, name: project.name });
    setNewProjectName(project.name);
  };

  const handleSaveProjectName = async () => {
    if (!editingProject || !newProjectName.trim()) return;
    
    try {
      await projectsService.updateProject(editingProject.id, { name: newProjectName.trim() });
      await loadProjects();
      message.success('Project renamed successfully');
      setEditingProject(null);
      setNewProjectName('');
    } catch (error) {
      console.error('Error updating project:', error);
      message.error('Failed to rename project');
    }
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setNewProjectName('');
  };

  const handleDeleteProjectClick = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'Delete Project',
      content: `Are you sure you want to delete "${project.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await projectsService.deleteProject(project.project_uuid);
          await loadProjects();
          message.success('Project deleted successfully');
          
          // Navigate to overview if we're currently viewing the deleted project
          if (project.project_uuid === selectedProject?.project_uuid) {
            navigate('/overview');
          }
        } catch (error) {
          console.error('Error deleting project:', error);
          message.error('Failed to delete project');
        }
      },
    });
  };

  const handleNewProject = () => {
    setProjectDropdownOpen(false);
    setShowCreateProjectModal(true);
  };

  const handleProjectCreated = async (newProject: any) => {
    // Reload projects to update the context
    await loadProjects();
    
    // Navigate to the new project's assistant page
    if (newProject?.project_uuid) {
      navigate(navigationService.project(newProject.project_uuid));
    }
  };

  const handleEditProjectSettings = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setProjectDropdownOpen(false);
    setProjectToEdit(project);
    setShowEditProjectModal(true);
  };

  const handleProjectUpdated = async (updatedProject: Project) => {
    // Reload projects to update the context
    await loadProjects();
  };

  const handleProjectDeleted = async (deletedProjectId: string) => {
    // Reload projects to update the context
    await loadProjects();
    
    // Navigate to overview if we're currently viewing the deleted project
    if (selectedProject?.project_uuid === deletedProjectId) {
      navigate('/');
    }
  };

  // Template handlers
  // Template creation handlers - REMOVED (Interest Request and Brief no longer needed)

  const handleEditTemplate = (template: Resource) => {
    setTemplateToEdit(template);
    setTemplateTitle(template.title);
    setShowTemplateStudio(true);
  };

  const handleTemplateStudioSave = async (title: string, content: any) => {
    console.log('🔍 [ProjectHeader] Saving template:', {
      title,
      content,
      contentType: typeof content,
      isEditing: !!templateToEdit,
      templateToEdit: templateToEdit?.id,
      projectId: selectedProject?.project_uuid
    });
    
    try {
      if (templateToEdit) {
        // Update existing template
        console.log('🔄 [ProjectHeader] Updating existing template:', templateToEdit.id);
        await resourcesService.updateResource(templateToEdit.id, {
          title,
          content,
        });
        message.success('Template updated successfully!');
      } else {
        // Create new template
        console.log('➕ [ProjectHeader] Creating new template for project:', selectedProject?.project_uuid);
        const result = await resourcesService.createResource({
          title,
          content,
          project_id: selectedProject?.project_uuid,
        });
        console.log('✅ [ProjectHeader] Template created:', result);
        message.success('Template created successfully!');
      }
      
      setShowTemplateStudio(false);
      setTemplateToEdit(null);
      setTemplateTitle('');
      fetchProjectTemplates(); // Refresh templates
    } catch (error) {
      console.error('❌ [ProjectHeader] Error saving template:', error);
      message.error('Failed to save template');
    }
  };

  const handleTemplateStudioClose = () => {
    setShowTemplateStudio(false);
    setTemplateToEdit(null);
    setTemplateTitle('');
  };

  const handleTemplateDelete = async (resourceId: string) => {
    try {
      await resourcesService.deleteResource(resourceId);
      message.success('Template deleted successfully!');
      // Refresh the templates list
      fetchProjectTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      message.error('Failed to delete template');
      throw error; // Re-throw to let the modal handle the error state
    }
  };






  // Render modals that should always be available
  const modals = (
    <>
      {/* Project Creation Modal */}
      <ProjectOverlay
        isOpen={showCreateProjectModal}
        project={null}
        onClose={() => setShowCreateProjectModal(false)}
        onSuccess={handleProjectCreated}
      />
      
      {/* Project Edit Overlay */}
      <ProjectOverlay
        isOpen={showEditProjectModal}
        project={projectToEdit}
        onClose={() => {
          setShowEditProjectModal(false);
          setProjectToEdit(null);
        }}
        onSuccess={handleProjectUpdated}
        onDelete={handleProjectDeleted}
      />
      
      {/* Template Studio Modal */}
      <ResourceStudioModal
        isOpen={showTemplateStudio}
        onClose={handleTemplateStudioClose}
        onSave={handleTemplateStudioSave}
        onDelete={handleTemplateDelete}
        initialTitle={templateTitle}
        initialContent={templateToEdit?.content || ''}
        resourceId={templateToEdit?.id}
      />
    </>
  );

  // Show minimal header when no project selected
  if (!selectedProject) {
    return (
      <>
        <HeaderControls>
          <HeaderLeft>
            <HeaderButton 
              onClick={() => setShowCreateProjectModal(true)}
              title="Create your first project"
              style={{ 
                minWidth: '100px',
                justifyContent: 'space-between'
              }}
            >
              <span style={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                flex: 1,
                textAlign: 'left'
              }}>
                Create Project
              </span>
              <PlusIcon style={{ width: '12px', height: '12px', flexShrink: 0 }} />
            </HeaderButton>
          </HeaderLeft>
        </HeaderControls>
        {modals}
      </>
    );
  }

  return (
    <>
    <style>
      {`
        .project-dropdown-item:hover .project-actions {
          opacity: 1 !important;
        }
        button:hover .project-edit-button {
          opacity: 1 !important;
        }
        .project-item:hover .project-edit-button {
          opacity: 1 !important;
        }
      `}
    </style>
    <HeaderControls>
      <HeaderLeft>
          {/* Project Button */}
          <HeaderButton 
            data-project-dropdown
            onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
            title="Select project"
            style={{ 
              minWidth: '100px',
              justifyContent: 'space-between'
            }}
          >
            <span style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              flex: 1,
              textAlign: 'left'
            }}>
              {selectedProject?.name || 'Select Project'}
            </span>
            <ChevronDownIcon style={{ width: '12px', height: '12px', flexShrink: 0 }} />
          </HeaderButton>
          
      </HeaderLeft>
      
      <ProjectHeaderRight>
        {/* Workflow Button with Popover */}
        {/* {selectedProject && (
          <WorkflowPopover 
            open={workflowPopoverOpen}
            onOpenChange={setWorkflowPopoverOpen}
          >
            <HeaderButton
              onClick={() => setWorkflowPopoverOpen(!workflowPopoverOpen)}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                color: selectedProject.has_workflow ? colors.palette.indigo[600] : colors.text.secondary,
                background: selectedProject.has_workflow ? `${colors.palette.indigo[50]}80` : 'transparent',
                border: selectedProject.has_workflow ? `1px solid ${colors.palette.indigo[200]}` : 'none',
                borderRadius: '6px'
              }}
            >
              <CollectionIcon style={{ width: '12px', height: '12px' }} />
              Workflow
            </HeaderButton>
          </WorkflowPopover>
        )} */}
        
      </ProjectHeaderRight>
      
    </HeaderControls>
    
    {/* Project Dropdown - Positioned outside header to avoid clipping */}
    {projectDropdownOpen && (
      <div 
        data-project-dropdown
        style={{
          position: 'fixed',
          top: '50px',
          left: '16px',
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          minWidth: '240px',
          zIndex: 9999,
          maxHeight: '320px',
          overflowY: 'auto',
          pointerEvents: 'auto'
        }}>
        {projects.map((project) => (
          <ProjectMenuItem
            key={project.project_uuid}
            className="project-item"
            onClick={() => {
              console.log('🔄 [ProjectHeader] Project clicked:', project.name);
              handleProjectSelect(project);
            }}
            style={{
              fontWeight: project.project_uuid === selectedProject?.project_uuid ? '500' : '400',
              justifyContent: 'space-between',
              position: 'relative'
            }}
          >
            <span>{project.name}</span>
            
            <div
              onClick={(e) => {
                e.stopPropagation();
                console.log('🔄 [ProjectHeader] Settings clicked for:', project.name);
                setProjectToEdit(project);
                setShowEditProjectModal(true);
              }}
              title="Project settings"
              className="project-edit-button"
              style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: 0,
                transition: 'all 0.15s ease',
                padding: '2px'
              }}
              onMouseEnter={(e) => {
                const icon = e.currentTarget.querySelector('svg');
                if (icon) icon.style.color = '#6b7280';
              }}
              onMouseLeave={(e) => {
                const icon = e.currentTarget.querySelector('svg');
                if (icon) icon.style.color = '#9ca3af';
              }}
            >
              <CogIcon style={{ 
                width: '16px', 
                height: '16px', 
                color: '#9ca3af'
              }} />
            </div>
          </ProjectMenuItem>
        ))}
        
        <ProjectSeparator />
        <ProjectMenuItem onClick={() => {
          console.log('🔄 [ProjectHeader] New Project clicked');
          handleNewProject();
        }}>
          <PlusIcon />
          New Project
        </ProjectMenuItem>
      </div>
    )}
    
    {/* Project Creation Modal */}
    <ProjectOverlay
      isOpen={showCreateProjectModal}
      project={null}
      onClose={() => setShowCreateProjectModal(false)}
      onSuccess={handleProjectCreated}
    />
    
    {/* Project Edit Overlay */}
    <ProjectOverlay
      isOpen={showEditProjectModal}
      project={projectToEdit}
      onClose={() => {
        setShowEditProjectModal(false);
        setProjectToEdit(null);
      }}
      onSuccess={handleProjectUpdated}
      onDelete={handleProjectDeleted}
    />
    
    {/* Template Studio Modal */}
      <ResourceStudioModal
        isOpen={showTemplateStudio}
        onClose={handleTemplateStudioClose}
        onSave={handleTemplateStudioSave}
        onDelete={handleTemplateDelete}
        initialTitle={templateTitle}
        initialContent={templateToEdit?.content || ''}
        resourceId={templateToEdit?.id}
      />
    
    </>
  );
};

export const ProjectHeader = React.memo(ProjectHeaderComponent, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return (
    prevProps.tables === nextProps.tables &&
    prevProps.activeTableId === nextProps.activeTableId &&
    prevProps.tablesLoading === nextProps.tablesLoading &&
    prevProps.onStageClick === nextProps.onStageClick &&
    prevProps.onCreateStage === nextProps.onCreateStage &&
    prevProps.onDeleteTable === nextProps.onDeleteTable &&
    prevProps.onManageTemplates === nextProps.onManageTemplates &&
    prevProps.onSendOutreach === nextProps.onSendOutreach &&
    prevProps.currentTableCreators === nextProps.currentTableCreators &&
    prevProps.onAnalyzeOutreach === nextProps.onAnalyzeOutreach
  );
});
